import { AfterViewInit, Directive, ElementRef, EventEmitter, HostListener, inject, Output } from '@angular/core';
import { SideMenuService } from '@app/layout/side-menu/services/side-menu.service';
import { FormFilterTableControlService } from '@app/modules/table/services/form-filter-table-control.service';
import { FuiDestroyService } from '@services/destroy.service';
import { takeUntil } from 'rxjs';

/**
 * Директива расчитывает сколько чипсов в фильтре нужно показать/скрыть
 * в зависимости от размера контейнера. Реагирует на window.resize, боковое меню и изменение значений фильтра.
 */
@Directive({
    selector: '[appOrganizeChips]',
})
export class OrganizeChipsDirective implements AfterViewInit {
    readonly #sideMenuService: SideMenuService = inject(SideMenuService);
    readonly #formFilter: FormFilterTableControlService = inject(FormFilterTableControlService);
    readonly #destroy$: FuiDestroyService = inject(FuiDestroyService);

    /** Чипсы */
    public items: HTMLElement[];
    /** Контейнер */
    public container: HTMLElement;

    /** Отступ у чипсов */
    #CHIP_MARGIN = 8;

    constructor(private el: ElementRef) {}

    /**
     * Сколько чипсов скрыто
     */
    @Output() chipsHidden = new EventEmitter<number>();

    @HostListener('window:resize')
    onResize() {
        this.organizeChips();
    }

    ngAfterViewInit() {
        this.container = this.el.nativeElement;
        this.items = Array.from(this.container.children) as HTMLElement[];
        this.organizeChips();

        // Подписываемся на изменение бокового меню
        this.#sideMenuService.isFixedSidenav$.pipe(takeUntil(this.#destroy$)).subscribe(() => {
            requestAnimationFrame(() => {
                this.organizeChips();
            });
        });

        // Подписываемся на изменение фильтра
        this.#formFilter.filterChanged$.pipe(takeUntil(this.#destroy$)).subscribe(() => {
            requestAnimationFrame(() => {
                this.organizeChips();
            });
        });
    }

    organizeChips() {
        // Сначала скроем все элементы чтобы посчитать возможную ширину контейнера
        this.items.forEach((element) => (element.style.display = 'none'));
        this.el.nativeElement.style.width = '100%';
        const containerWidth = this.el.nativeElement.clientWidth;

        // Теперь покажем все элементы, на случай если ранее что-то скрывалось
        this.items.forEach((element) => (element.style.display = 'flex'));
        // Установим автоширину, чтобы не занимать все свободное место
        this.el.nativeElement.style.width = 'auto';

        let totalWidth = 0;
        let lastIndexToFit = -1;

        for (let i = 0; i < this.items.length; i++) {
            const item = this.items[i];
            const itemWidth = item.clientWidth + this.#CHIP_MARGIN;

            // Добавить ширину текущего элемента к общей ширине
            totalWidth += itemWidth;

            // Если ширина превышает ширину контейнера, останавливаемся
            if (totalWidth > containerWidth) {
                lastIndexToFit = i - 1;
                break;
            }
        }

        // Если последний элемент, который помещается, - это последний элемент всего списка, return
        if (lastIndexToFit === -1 || lastIndexToFit === this.items.length - 1) {
            this.chipsHidden.emit(0);
            return;
        }

        const elementsToRemove = this.items.slice(lastIndexToFit + 1);
        this.chipsHidden.emit(elementsToRemove.length);
        elementsToRemove.forEach((element) => (element.style.display = 'none'));
    }
}
